<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

const props = defineProps({
    modelValue: { type: [String, Blob, Array, Object], default: '' },
    inputId: { type: String, default: '' },
    filename: { type: String, default: '' },
    label: { type: String, default: '' },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    marginTop: { type: String, default: '0' },
    marginBottom: { type: String, default: '0' },
    marginLeft: { type: String, default: '0' },
    marginRight: { type: String, default: '0' },
    labelRightmargin: { type: String, default: '2rem' },
    visible: { type: Boolean, default: true },
    height: { type: String, default: '2.4rem' },
    width: { type: String, default: '100%' },
    readonly: { type: Boolean, default: false },
    errmsg: { type: String, default: '' }
});

const emit = defineEmits(['update:modelValue']);
const isCanvasEmpty = ref(true);
const containerRef = ref(null);
const canvasRef = ref(null);
const isDrawing = ref(false);
const ValidationMsg = ref('');
let baseUrl = import.meta.env.VITE_APP_API_URL;

let ctx;
let dpr = 1;
let lastX = 0;
let lastY = 0;
let ro;

const previewSrc = ref('');
const hasPreview = computed(() => !!previewSrc.value);

const setPreviewFromValue = async (val) => {
    if (!val) {
        previewSrc.value = '';
        return;
    }

    if (Array.isArray(val) && val.length && val[0]?.filename) {
        previewSrc.value = `${baseUrl}/view/${val[0].filename}`;
        return;
    }

    if (typeof val === 'string') {
        previewSrc.value = val;
        return;
    }
    if (val instanceof Blob) {
        const dataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(val);
        });
        previewSrc.value = dataUrl;
    }
};

const setupCanvasSize = () => {
    if (!canvasRef.value || !ctx) return;
    const host = containerRef.value ?? canvasRef.value;
    const rect = host.getBoundingClientRect();
    dpr = window.devicePixelRatio || 1;

    const cssW = Math.max(1, Math.round(rect.width));
    const cssH = Math.max(1, Math.round(rect.height));
    const bufW = Math.max(1, Math.round(cssW * dpr));
    const bufH = Math.max(1, Math.round(cssH * dpr));

    canvasRef.value.width = bufW;
    canvasRef.value.height = bufH;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    // 스타일 재설정
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000';
};

// 리사이즈 시 기존 드로잉 유지
const resizeWithKeep = () => {
    if (!canvasRef.value || !ctx || hasPreview.value) return;

    const src = canvasRef.value;
    const backup = document.createElement('canvas');
    backup.width = src.width;
    backup.height = src.height;
    backup.getContext('2d').drawImage(src, 0, 0);

    setupCanvasSize();

    // 목적지는 CSS px로 그리기
    const rect = (containerRef.value ?? canvasRef.value).getBoundingClientRect();
    ctx.drawImage(backup, 0, 0, backup.width, backup.height, 0, 0, Math.round(rect.width), Math.round(rect.height));
};

const handleResize = () => {
    resizeWithKeep();
};

onMounted(async () => {
    await nextTick();
    if (!canvasRef.value) return;

    ctx = canvasRef.value.getContext('2d', { willReadFrequently: true });
    setupCanvasSize();

    window.addEventListener('resize', handleResize);

    if (window.ResizeObserver && (containerRef.value ?? canvasRef.value)) {
        ro = new ResizeObserver(() => {
            if (!hasPreview.value) resizeWithKeep();
        });
        ro.observe(containerRef.value ?? canvasRef.value);
    }
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
    if (ro) ro.disconnect();
});

const getPoint = (e) => {
    const rect = canvasRef.value.getBoundingClientRect();
    const clientX = e.clientX ?? (e.touches && e.touches[0].clientX);
    const clientY = e.clientY ?? (e.touches && e.touches[0].clientY);
    return { x: clientX - rect.left, y: clientY - rect.top };
};

const startDrawing = (e) => {
    if (hasPreview.value || props.readonly || props.disabled || !canvasRef.value || !ctx) return;
    //e.preventDefault();
    isDrawing.value = true;
    isCanvasEmpty.value = false;

    const { x, y } = getPoint(e);
    lastX = x;
    lastY = y;
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
};

const draw = (e) => {
    if (!isDrawing.value || hasPreview.value || props.readonly || props.disabled || !canvasRef.value || !ctx) return;
    //e.preventDefault();

    const { x, y } = getPoint(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastX = x;
    lastY = y;
};

const stopDrawing = () => {
    if (isDrawing.value) isDrawing.value = false;
};

const emitSignatureSrc = () => {
    if (!canvasRef.value) return;
    const dataUrl = canvasRef.value.toDataURL('image/png');
    emit('update:modelValue', dataUrl);
    previewSrc.value = dataUrl;
};

const clearSignature = () => {
    if (props.readonly || props.disabled || !canvasRef.value || !ctx) return;
    previewSrc.value = '';
    ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height);
    isCanvasEmpty.value = true;
    emit('update:modelValue', '');
};

const getSignatureBlob = async () =>
    new Promise((resolve) => {
        canvasRef.value.toBlob((blob) => resolve(blob), 'image/png');
    });

const emitSignature = async () => {
    const blob = await getSignatureBlob();
    emit('update:modelValue', blob);
};

const isSignEmpty = () => {
    if (!canvasRef.value || !ctx) return true;
    const pixels = ctx.getImageData(0, 0, canvasRef.value.width, canvasRef.value.height).data;
    for (let i = 3; i < pixels.length; i += 4) if (pixels[i] !== 0) return false;
    return true;
};

const getSignatureAsMultipart = async (fileName = Date.now() + '.png') => {
    if (!canvasRef.value || !ctx) return null;

    if (typeof isSignEmpty === 'function' && isSignEmpty()) return null;

    const blob = await getSignatureBlob();
    if (!blob || blob.size === 0) return null;

    let name = String(fileName || Date.now() + '.png');
    if (!/\.(png|jpg|jpeg|webp)$/i.test(name)) name += '.png';

    // Blob -> File
    const file = new File([blob], name, { type: 'image/png', lastModified: Date.now() });

    return {
        delete: false,
        file,
        fileid: '',
        filename: file.name,
        name: file.name,
        size: file.size,
        uploaded: false,
        type: file.type
    };
};

watch(
    () => props.modelValue,
    (v) => {
        setPreviewFromValue(v);
    },
    { immediate: true }
);
watch(
    () => props.errmsg,
    (v) => {
        ValidationMsg.value = v || '';
    }
);

watch(
    () => props.height,
    async () => {
        await nextTick();
        if (!hasPreview.value) resizeWithKeep();
    }
);

watch(
    () => hasPreview.value,
    async (v) => {
        if (!v) {
            await nextTick();
            resizeWithKeep();
        }
    }
);

defineExpose({ getSignatureBlob, isSignEmpty, emitSignature, getSignatureAsMultipart });
</script>

<template>
    <div class="signature-component" :style="{ marginTop, marginBottom, marginLeft, marginRight }">
        <label v-if="label" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label>

        <div v-if="!hasPreview" ref="containerRef" class="canvas-container" :style="{ height: props.height }">
            <canvas ref="canvasRef" @mousedown="startDrawing" @mousemove="draw" @mouseup="stopDrawing" @mouseout="stopDrawing" @touchstart.passive="startDrawing" @touchmove.passive="draw" @touchend.passive="stopDrawing"></canvas>

            <div v-if="isCanvasEmpty && !props.disabled && !props.readonly" class="placeholder">서명</div>

            <button :disabled="props.readonly || props.disabled" @click="clearSignature" class="clear-button">
                <i class="pi pi-refresh"></i>
            </button>
        </div>

        <div v-else class="signature-preview-wrap" :style="{ height: props.height }">
            <img :src="previewSrc" alt="서명 미리보기" class="signature-preview" />
        </div>
    </div>
</template>

<style scoped>
.signature-component {
    display: flex;
    flex-direction: column;
}

.required-asterisk {
    color: #f87171;
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
    user-select: none;
    white-space: nowrap;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.canvas-container {
    position: relative;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.5rem;
    overflow: hidden;
}

.canvas-container canvas {
    width: 100%;
    height: 100%;
    display: block;
    touch-action: none;
    user-select: none;
    -webkit-user-select: none;
    cursor: crosshair;
}

.clear-button {
    position: absolute;
    top: 0.3rem;
    right: 0.3rem;
    padding: 0.35rem;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    font-size: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
}
.clear-button:hover {
    background-color: rgba(209, 213, 219, 0.8);
}
.clear-button:disabled {
    cursor: not-allowed;
    opacity: 0.7;
}
.clear-button i {
    font-size: 0.875rem;
}

.signature-preview-wrap {
    border: 0.1rem solid #d1d5db;
    border-radius: 0.5rem;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
}
.signature-preview {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    display: block;
}

.placeholder {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #cccccc;
    font-size: 1.5rem; /* 원하는 크기로 조절하세요 */
    font-family: 'SCoreDream500';
    pointer-events: none; /* 캔버스 위에 있어도 드로잉이 가능하게 함 */
    user-select: none;
}
</style>
